import React from "react";
import { useFormikContext } from "formik";
import { CURRENCY_OPTIONS } from "components/FormerEditor/common/constants/currency";
import { getFieldPath } from "utils/form";
import {
  formattedPriceToUnitAmount,
  formatUnitAmountFloat,
  getConfigForCurrency
} from "components/FormerEditor/common/currency";
import CurrencyInput from "components/FormerEditor/CurrencyInput";
import Select from "components/Form/fields/Select";
import get from "lodash/get";
import { removeProductPrice } from "utils/product";
import classnames from "classnames";
import { BILLING_PERIOD_OPTIONS } from "utils/constants/payment";
import HoverIconButton from "components/HoverIconButton";
import { findIndexForModel } from "utils/data";
import Analytics, {
  REMOVE_PRICE,
  SELECT_BILLING_INTERVAL,
  SELECT_CURRENCY
} from "utils/analytics";

const PriceTableRow = ({ price, productIndex, isLast, tracking }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const currencyConfig = getConfigForCurrency(price.currency);
  const maskOptions = {
    prefix: currencyConfig.symbol
  };

  const productPricesPath = `products[${productIndex}].prices`;
  const prices = get(values, productPricesPath);

  const priceIndex = findIndexForModel(price, prices);

  if (priceIndex === -1) {
    throw new Error("Price needs id");
  }

  const rootPath = `products[${productIndex}].prices[${priceIndex}]`;
  const formattedPriceName = getFieldPath(rootPath, "formattedPrice");

  const inputProps = {
    placeholder: `${currencyConfig.symbol}0.00`,
    type: "text",
    className:
      "f5 border-box input-reset bw1 pa2 db w-100 br2 lh-copy hairline-1",
    value: price.formattedPrice,
    onChange: ({ currentTarget }) => {
      setFieldValue(formattedPriceName, currentTarget.value);
      if (!get(touched, formattedPriceName)) {
        setFieldTouched(formattedPriceName, true);
      }
    }
  };

  const currencyPath = getFieldPath(rootPath, "currency");
  const selectCurrencyProps = {
    options: CURRENCY_OPTIONS,
    stateKey: currencyPath,
    value: price.currency,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      if (selection !== price.currency) {
        const currentUnitAmount = formattedPriceToUnitAmount({
          formattedPrice: price.formattedPrice,
          currency: price.currency
        });
        const newFormattedAmount = formatUnitAmountFloat({
          amount: currentUnitAmount,
          currency: selection
        });

        /**
         * Set new currency
         */
        setFieldValue(currencyPath, selection);
        /**
         * Set new amount
         */
        setFieldValue(formattedPriceName, newFormattedAmount);

        Analytics.track(SELECT_CURRENCY, { ...tracking, currency: selection });
        if (!get(touched, currencyPath)) {
          setFieldTouched(currencyPath, true);
        }
      }
    },
    customClasses: {
      container: "w-100 min-w3",
      input:
        "border-box input-reset ba br2 b--black-20 pa2 db w-100 pointer hairline-1"
    }
  };
  const intervalPath = getFieldPath(rootPath, "recurring.interval");
  const selectIntervalProps = {
    options: BILLING_PERIOD_OPTIONS,
    stateKey: intervalPath,
    value: get(price, "recurring.interval"),
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      Analytics.track(SELECT_BILLING_INTERVAL, {
        ...tracking,
        billing_interval: selection
      });
      setFieldValue(intervalPath, selection);
      if (!get(touched, intervalPath)) {
        setFieldTouched(intervalPath, true);
      }
    },
    customClasses: {
      container: "w-100",
      input:
        "border-box input-reset ba br2 b--black-20 pa2 db w-100 pointer hairline-1"
    }
  };

  return (
    <tr key={priceIndex} className="bg-white">
      <td
        className={classnames("hairline-1 bb bl", {
          "br--left br--bottom br2": isLast
        })}
      >
        <div className="pa1">
          <Select {...selectCurrencyProps} />
        </div>
      </td>
      <td className="bb hairline-1">
        <div className="pa1">
          <CurrencyInput
            key={price.currency}
            maskOptions={maskOptions}
            {...inputProps}
          />
        </div>
      </td>
      <td
        className={classnames("hairline-1 bb br", {
          "br--right br--bottom br2": isLast
        })}
      >
        <div className="pa1 price__interval--select min-w3-5 w-auto-ns">
          <Select {...selectIntervalProps} />
        </div>
      </td>
      <td className="">
        <div className="flex items-center justify-center">
          <HoverIconButton
            type="close"
            tooltip="Remove price"
            onClick={removeProductPrice({
              products: values.products,
              productIndex,
              priceIndex,
              setFieldTouched,
              setFieldValue,
              tracking: {
                event: REMOVE_PRICE,
                data: tracking
              }
            })}
          />
        </div>
      </td>
    </tr>
  );
};

export default PriceTableRow;
